import { createAsyncThunk } from "@reduxjs/toolkit";
import API from "../../../network/API";
import AxiosClient from "../../../network/axiosClient";
import {
    CreateSwapRequest,
    DeleteSwapScheduleRequest,
    GetMyScheduleDetailsRequest,
    GetMySchedulesRequest,
    GetSwapEmployeesRequest,
    GetTodaysSchedulesRequest,
    IRejectWithValue,
    PhpResponse,
    ScheduleV2,
    SwapEmployeeV2,
    UpdateSwapScheduleRequest,
} from "../../../types";
import { phpClient } from "../../../network";
// import { ISchedule } from './models/schedule';
import { format, addDays } from "date-fns";

/**
 * NEW APIs
 */

export const getShiftsByDate = createAsyncThunk(
    "/schedules/by-date",
    async (data: { startDatetime: string; endDatetime: string }, thunkAPI) => {
        const { rejectWithValue } = thunkAPI;
        try {
            const response = await API.get("/schedule", {
                params: data,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getShiftsByDateV2 = createAsyncThunk<ScheduleV2[], { startDatetime: string; endDatetime: string }, IRejectWithValue>(
    "/schedules/by-date-v2",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<ScheduleV2[]>>({
                url: "/Schedule/emp_schedule",
                data,
            });
            return response.data || [];
        } catch (e) {
            return rejectWithValue((e as Error).message || "An error occurred");
        }
    });

export const getSwapEmployees = createAsyncThunk(
    "/schedules/swap_employees",
    async (
        data: {
            request_type: number;
            start_date: string;
            end_date: string;
            assigned_shift_code: string;
            department_code: string;
            group_code: string[];
            name_search: string;
            schedule_id: string;
        },
        thunkAPI
    ) => {
        const { rejectWithValue } = thunkAPI;
        try {
            const response = await API.post("/schedule/swap_employees", data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getSwapEmployeesV2 = createAsyncThunk<SwapEmployeeV2[], GetSwapEmployeesRequest, IRejectWithValue>(
    "/schedules/swap_employees_v2",
    async (data, { rejectWithValue }) => {
        try {
            const res = await phpClient.post<PhpResponse<SwapEmployeeV2[]>>({
                url: "/schedule/swappable_employees",
                data,
            });
            return res.data || [];
        } catch (e) {
            return rejectWithValue((e as Error).message || "An error occurred");
        }
    }
);

export const createSwapRequestV2 = createAsyncThunk<void, CreateSwapRequest, IRejectWithValue>(
    "/schedules/cannotwork_request_v2",
    async (data, { rejectWithValue }) => {
        try {
            await phpClient.post<void>({
                url: "/schedule/create_swap_request",
                data,
            });
        } catch (e) {
            return rejectWithValue((e as Error).message || "An error occurred");
        }
    }
);

export const createSwapRequest = createAsyncThunk(
    "/schedules/cannotwork_request",
    async (
        data: { scheduleId: number; scheduleIds: string[]; employeeIds: string[]; notes: string; requestType: number },
        thunkAPI
    ) => {
        const { rejectWithValue } = thunkAPI;
        try {
            const response = await API.post("/schedule/cannotwork_request", data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const updateSwapRequest = createAsyncThunk(
    "/schedules/swap/update",
    async (data: UpdateSwapScheduleRequest, thunkAPI) => {
        const { rejectWithValue } = thunkAPI;
        // const { scheduleId: id, ...rest } = data;
        try {
            const { referenceId, ...body } = data;
            const response = await API.post(`/schedule/swap/${referenceId}`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const deleteSwapRequest = createAsyncThunk(
    "/schedules/swap/delete",
    async (data: DeleteSwapScheduleRequest, thunkAPI) => {
        const { rejectWithValue } = thunkAPI;
        // const { scheduleId: id, ...rest } = data;
        try {
            const { scheduleId } = data;
            const response = await API.delete(`/schedule/cannotwork_request/${scheduleId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getMySchedules = createAsyncThunk<ScheduleV2[], GetMySchedulesRequest, IRejectWithValue>(
    "schedule/getMySchedules",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<ScheduleV2[]>>({
                url: "/Schedule/my_schedules",
                data,
            });
            return response.data || [];
        } catch (e) {
            return rejectWithValue((e as Error).message || "An error occurred");
        }
    }
);

export const getMyTodaysSchedule = createAsyncThunk<ScheduleV2 | undefined, GetTodaysSchedulesRequest, IRejectWithValue>(
    "schedule/getMyTodaySchedules",
    async (_, thunkAPI) => {
        const today = format(new Date(), "yyyy-MM-dd");
        const state = thunkAPI.getState() as any;
        const os_emp_account_id = state.user?.user?.id;
        const localStorageKey = `myTodaysSchedule_${os_emp_account_id}`;

        // // Check localStorage for cached schedule
        // try {
        //     const cached = localStorage.getItem(localStorageKey);
        //     if (cached) {
        //         const parsed = JSON.parse(cached);
        //         if (parsed.date === today) {
        //             console.log("Loaded today's schedule from localStorage for user:", os_emp_account_id);
        //             return parsed.data;
        //         }
        //     }
        // } catch (e) {
        //     // Ignore localStorage errors and proceed to fetch
        // }

        // Always fetch from server on first call of the day
        console.log("Fetching today's schedule from backend for user:", os_emp_account_id);
        const data: GetMySchedulesRequest = {
            start_date: today,
            end_date: today,
            published_only: "true",
        };

        try {
            const response = await phpClient.post<PhpResponse<ScheduleV2[]>>({
                url: "/Schedule/my_schedules",
                data,
            });
            const schedule = response.data?.[0];
            // Cache the result in localStorage
            try {
                localStorage.setItem(localStorageKey, JSON.stringify({ date: today, data: schedule }));
            } catch (e) {
                // Ignore localStorage errors
            }
            return schedule;
        } catch (e) {
            return thunkAPI.rejectWithValue((e as Error).message || "An error occurred");
        }
    }
);

export const getMyScheduleDetails = createAsyncThunk<
    ScheduleV2 | undefined,
    GetMyScheduleDetailsRequest,
    IRejectWithValue
>("schedule/getMyScheduleDetails", async (data, { rejectWithValue }) => {
    try {
        const { schedule_id } = data;
        const response = await phpClient.get<PhpResponse<ScheduleV2[]>>({
            url: `/Schedule/my_schedules_details/${schedule_id}`,
        });
        return response.data?.[0];
    } catch (e) {
        return rejectWithValue((e as Error).message || "An error occurred");
    }
});
